/* eslint-disable camelcase */
export interface Recording {
  currentTime: number;
  duration: number;
  device: string;
  site_id: number;
  account_id: number;
  organization_id: number;
  user_id: number;
  id: number;
  url: string;
  shared: boolean;
  skipPauses: boolean;
  jumpstart_to_index: number | null;
  created_epoch_time: number;
  allowlisted_domains: Array<string>;
  page_visit_recordings: Array<PageVisitRecording>;
  public_url: string;
  visitor_uuid: string;
  is_first_seen: boolean;
  country_code: string | null;
  country_name: string;
  window_size: number[];
  browser: string;
  os: string;
  referrer: string | null;
  action_count: number;
  effective_plan: string;
  user_attributes: Array<RawUserAttribute>;
  site_features: Array<string>;
  relevance_score: number;
  page_count: number;
  landing_page_url: string;
  exit_page_url: string;
  watched: boolean;
  autotags?: Array<string>;
  worth_watching?: boolean;
  account_created?: number;
  user_access?: string;
  protocol: string;
  organization_name: string;
  event_markers?: Array<EventMarker>;
  tab_count: number;
  frustration_score: number;
}

export enum DataTypeEnum {
  EMAIL = 'email',
  BOOLEAN = 'bool',
  DATE = 'date',
  STRING = 'str',
  NUMBER = 'number',
  UNKNOWN = 'unknown',
}

export type DataType =
  | DataTypeEnum.EMAIL
  | DataTypeEnum.BOOLEAN
  | DataTypeEnum.DATE
  | DataTypeEnum.STRING
  | DataTypeEnum.NUMBER
  | DataTypeEnum.UNKNOWN;

export interface UserAttribute {
  datatype: DataType;
  name: string;
  value: string | number;
}

export interface RawUserAttribute {
  datatype: DataType;
  name: string;
  [key: string]: string | number;
}

export interface EventMarker {
  type: EventType;
  progress_pos: string;
  time: number;
  key: string;
  name?: BehaviorName | string; // It can look like "poll.send.id:155923.r_id:" for poll
  progress_duration?: string;
  subtype?: BehaviorName;
  is_highlighted?: boolean;
  is_new_tab?: boolean;
  is_visited_tab?: boolean;
  tab_index?: number;
  url?: string;
  selector?: string;
  text?: string;
  error_text?: string;
  value?: string;
  suppression?: string;
  w?: number;
  h?: number;
  duration?: number;
  description?: string;
  show_in_timeline?: boolean;
  autotag_properties?: {
    type: string;
    subtype?: string;
    id: string | number;
    e?: number;
    response_id?: string | number | null;
  };
}

export type Content = {
  children: Array<unknown>;
  docType: string;
  rootId: number;
};

type WebResource = {
  original_url: string;
  content_hash: string;
  url_hash: string;
};

export type ServerResponse = {
  page_visit_url: string;
  content: string;
  page: number;
  events: Array<unknown>;
  allowlisted_domains: Array<string>;

  start_page_content_identifier: string;
  recording: {
    id: number;
    device: string;
    web_resources: Array<WebResource>;
  };
};

export type PageVisitData = {
  page: number;
  pageVisitUrl: string;
  content: Content;
  webResources: Array<WebResource>;
  allowlistedDomains: Array<string>;
  recordingId: number;
  baseTag: HTMLHeadElement;
  startPageContentIdentifier: string;
};

export type Trace = Array<{
  functionName: string;
  fileName: string;
  lineNumber: number;
  columnNumber: number;
}>;

export interface PageVisitEvent {
  id: number;
  text?: string;
  time: number;
  time_in_timeline: number;
  timestamp: number;
  type: string;
  w?: number;
  h?: number;
  a?: CustomEvent[];
  b?: CustomEvent[];
  c?: CustomEvent[];
  d?: CustomEvent[];
  trace?: Trace;
}

export interface ViewportResizeEvent {
  id: number;
  time: number;
  time_in_timeline: number;
  timestamp: number;
  type: string;
  w: number;
  h: number;
}

export interface PageVisitRecording {
  duration: number;
  events: PageVisitEvent[];
  page_content_md5: string;
  page_url: string;
  start_page_content_identifier: string;
  time_in_timeline: number;
  window_height: number;
  window_width: number;
  tab_index: number;
}

// For the sake of aggregating events in the actions panel, we have artificial
// events in a standalone enum.
export enum ArtificialEventTypeEnum {
  SELECTION = 'selection',
}

export enum HiddenEventTypeEnum {
  AUTOTAG_RECORDING = 'autotag_recording',
  SCROLL = 'scroll',
  HEATMAP_SCREENSHOT = 'heatmap_screenshot',
  INPUT_CHOICE_CHANGE = 'input_choice_change',
  SELECT_CHANGE = 'select_change',
  ERROR = 'error',
}

export enum EventTypeEnum {
  KEY_PRESS = 'key_press',
  MOUSE_CLICK = 'mouse_click',
  PAGE = 'page',
  VIEWPORT_RESIZE = 'viewport_resize',
  TAG_RECORDING = 'tag_recording',
}

export enum HiddenBehaviorNameEnum {
  MAD_MOUSE = 'madmouse',
  WILD_SCROLL = 'wildscroll',
}

export enum BehaviorNameEnum {
  RAGE_CLICK = 'rageclick',
  U_TURN = 'uturn',
  POLL = 'poll',
  FEEDBACK = 'feedback',
}

export type EventType = `${HiddenEventTypeEnum | EventTypeEnum}`;

export type BehaviorName = `${HiddenBehaviorNameEnum | BehaviorNameEnum}`;

export interface SectionInfo {
  index: number;
  pageIndex: number;
  tabIndex: number;
  startTimeInMs: number;
  startTimeInPercent: number;
  durationInMs: number;
  durationInPercent: number;
  url: string;
  isActive: boolean;
}

export type JumpDirectionType = 'backward' | 'forward';

export enum ModalStateEnum {
  CLOSE,
  KEYBOARD_SHORTCUT_OPEN,
  SETTINGS_OPEN,
  DELETE_RECORDING_OPEN,
  SHARE_RECORDING_OPEN,
}

export enum ItemType {
  HIGHLIGHT = 'highlight',
}

export interface TimelineNewItemMetadata {
  type: ItemType;
  time: number | null;
}

export interface TimelineActiveItem {
  type: ItemType;
  id: number;
}

export interface EventBoxTranslationOptions {
  page?: {
    tabNumber: number;
    totalTab: number;
  };
}
